import { useEffect } from 'react';
// @ts-ignore rpc-client-utils is resolved by bend at build time
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import getLang from 'I18n/utils/getLang';
import { useGetListingDetailsV3LazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc.hooks';
import { useGetOfferingDetails } from 'integrations-reconnect-lib';
import { TListingDetails } from '../types/listingDetails';
import { IntegrationProductType } from '../types/integrationProductType';

const getProductType = (listingDetails?: TListingDetails | null) => {
  const isMcpProduct = !!(
    listingDetails?.isMcpServer || listingDetails?.isMcpConnector
  );

  if (
    [
      IntegrationProductType.APP,
      IntegrationProductType.AGENT,
      IntegrationProductType.CONNECTOR,
      IntegrationProductType.WORKFLOW_TEMPLATE,
    ].includes(listingDetails?.productType as IntegrationProductType) &&
    !isMcpProduct
  ) {
    return listingDetails?.productType as IntegrationProductType;
  } else if (
    listingDetails?.productType === IntegrationProductType.APP &&
    isMcpProduct
  ) {
    return IntegrationProductType.CONNECTOR;
  }

  return IntegrationProductType.UNKNOWN;
};

const areListingDetailsProvided = (listingDetails?: TListingDetails) => {
  return (
    !!listingDetails?.listingId &&
    !!listingDetails?.productType &&
    listingDetails?.isMcpConnector !== undefined &&
    listingDetails?.isMcpServer !== undefined
  );
};

type TUseIntegrationProductTypeProps = {
  /**
   *  App ID. Is necessary for fetching of offering details. App ID is not required
   *  when `listingDetails` or at least `listingDetails.listingId` is provided
   * */
  appId?: number;

  /**
   * Listing details. Could be provided or fetched by the hook itself. If it is not provided
   * then `appId` or `listingDetails.listingId` is necessary for fetching.
   */
  listingDetails?: TListingDetails;

  /** Skip network requests */
  skip?: boolean;
};

type TUseListingDetailsReturn = {
  data: Omit<TListingDetails, 'productType'> & {
    productType: IntegrationProductType;
  };
  loading: boolean;
  error: unknown;
};

export const useListingDetails = ({
  appId,
  listingDetails: propsListingDetails,
  skip,
}: TUseIntegrationProductTypeProps) => {
  const propsListingId = propsListingDetails?.listingId;

  /**
   * Fetch offering for `listingId` or use given from `props.listingDetails.listingId`
   */
  const {
    data: offering,
    loading: offeringLoading,
    error: offeringError,
  } = useGetOfferingDetails({
    appId,
    skip: skip || !appId || !!propsListingId,
  });

  const listingId = offering?.listingId || propsListingId || 0;

  /**
   * Fetch of listing details by listing ID  or use given from `props.listingDetails`
   */
  const [
    fetchDetails,
    { data: detailsData, loading: detailsLoading, error: detailsError },
  ] = useGetListingDetailsV3LazyQuery();

  const listingDetails: TListingDetails | null = detailsData?.listing
    ? (convertFieldValueAvroToJson(detailsData.listing) as TListingDetails)
    : propsListingDetails || null;

  const hasListingDetailsProvided =
    areListingDetailsProvided(propsListingDetails);

  const hasDetailsRequestCompleted =
    !detailsLoading && !!(detailsError || detailsData?.listing);

  useEffect(() => {
    if (
      listingId &&
      !hasDetailsRequestCompleted &&
      !hasListingDetailsProvided &&
      !skip
    ) {
      fetchDetails({ variables: { listingId, language: getLang() } });
    }
  }, [
    fetchDetails,
    hasDetailsRequestCompleted,
    listingId,
    hasListingDetailsProvided,
    skip,
  ]);

  return {
    data: {
      ...listingDetails,
      listingId,
      productType: getProductType(listingDetails),
      isMcpConnector: !!listingDetails?.isMcpConnector,
      isMcpServer: !!listingDetails?.isMcpServer,
    },
    loading: offeringLoading || detailsLoading,
    error: offeringError || detailsError,
  } as TUseListingDetailsReturn;
};
